import { STATIONEN, reiseStand, type Reisestand, type Station } from './reise'

/**
 * Die Ballonreise als Karte.
 *
 * Keine Weltkarte mit Küstenlinien: Auf einem Tablet wären die Stationen dann
 * so klein, dass man sie nicht auseinanderhält. Stattdessen eine geschwungene
 * Route von links nach rechts, auf der man den Ballon und das nächste Ziel
 * sofort findet.
 *
 * Der zurückgelegte Teil der Strecke ist golden und durchgezogen, der Rest
 * blass gestrichelt. Das macht auf einen Blick klar, wie weit die Gruppe
 * gekommen ist, ohne dass eine Zahl danebenstehen muss.
 */
export function Reisekarte({ rundenGesamt }: { rundenGesamt: number }) {
  const stand = reiseStand(rundenGesamt)

  const ganzeRoute = glatterPfad(STATIONEN)
  const gereist = glatterPfad([
    ...STATIONEN.slice(0, stand.stationIndex + 1),
    { x: stand.x, y: stand.y },
  ])

  return (
    <div className="reise">
      {/* Der Ausschnitt zeigt genau das Band, in dem die Route liegt, plus
          Luft nach oben für Beschriftung und Ballon. Eine Zeichenfläche von
          0 bis 100 wäre zur Hälfte leer und die Karte doppelt so hoch. */}
      <svg className="reise__karte" viewBox="0 15 100 58" preserveAspectRatio="xMidYMid meet">
        <path
          d={ganzeRoute}
          fill="none"
          stroke="rgba(255,248,231,0.22)"
          strokeWidth="0.7"
          strokeDasharray="2 2.5"
          strokeLinecap="round"
        />
        <path
          d={gereist}
          fill="none"
          stroke="var(--gold)"
          strokeWidth="1.1"
          strokeLinecap="round"
        />

        {STATIONEN.map((s, i) => {
          const erreicht = i <= stand.stationIndex
          const istZiel = i === stand.stationIndex + 1
          return (
            <g key={s.name}>
              <circle
                cx={s.x}
                cy={s.y}
                r={erreicht ? 1.5 : 1}
                fill={erreicht ? 'var(--gold)' : 'rgba(255,248,231,0.28)'}
              />
              {istZiel && (
                <circle
                  cx={s.x}
                  cy={s.y}
                  r="2.6"
                  fill="none"
                  stroke="var(--gold)"
                  strokeWidth="0.5"
                  opacity="0.8"
                />
              )}
              {/* Nur das nächste Ziel beschriften. Die aktuelle Station steht
                  ohnehin groß unter der Karte, und zwei Namen nebeneinander
                  überlappen bei eng beieinander liegenden Stationen. */}
              {istZiel && <Beschriftung station={s} erreicht={erreicht} />}
            </g>
          )
        })}

        <g transform={`translate(${stand.x} ${stand.y}) scale(0.058) translate(-50 -110)`}>
          <path
            d="M50 6 C74 6 88 24 88 45 C88 64 72 78 56 88 L44 88 C28 78 12 64 12 45 C12 24 26 6 50 6 Z"
            fill="var(--rot)"
          />
          <ellipse cx="34" cy="30" rx="11" ry="15" fill="#fff" opacity="0.3" />
          <path d="M42 88 L44 98 M58 88 L56 98" stroke="#d9c49a" strokeWidth="2.4" fill="none" />
          <path d="M43 98 L57 98 L54 110 L46 110 Z" fill="#b5763a" />
        </g>
      </svg>

      <Reisetext stand={stand} />
    </div>
  )
}

/**
 * Stationsname über dem Punkt.
 *
 * Am linken und rechten Rand wird der Text verankert statt zentriert, sonst
 * ragt "Bad Essen" über die Zeichenfläche hinaus und wird abgeschnitten.
 */
function Beschriftung({ station, erreicht }: { station: Station; erreicht: boolean }) {
  const amLinkenRand = station.x < 14
  const amRechtenRand = station.x > 86

  return (
    <text
      x={amLinkenRand ? 0.5 : amRechtenRand ? 99.5 : station.x}
      y={station.y - 3.6}
      textAnchor={amLinkenRand ? 'start' : amRechtenRand ? 'end' : 'middle'}
      fontSize="3.1"
      fontWeight="700"
      fill={erreicht ? 'var(--gold)' : 'rgba(255,248,231,0.6)'}
    >
      {station.name}
    </text>
  )
}

/**
 * Weiche Kurve durch alle Punkte.
 *
 * Eine gerade Verbindung von Punkt zu Punkt sieht bei sechzehn Stationen aus
 * wie ein Kursdiagramm, nicht wie eine Reise. Die Kurve läuft durch die
 * Mittelpunkte der Strecken und nutzt die Stationen als Kontrollpunkte, das
 * ist der übliche Kniff für eine glatte Linie durch gegebene Punkte.
 */
function glatterPfad(punkte: Array<{ x: number; y: number }>): string {
  if (punkte.length === 0) return ''
  if (punkte.length === 1) return `M ${punkte[0]!.x} ${punkte[0]!.y}`

  let d = `M ${punkte[0]!.x} ${punkte[0]!.y}`
  for (let i = 1; i < punkte.length - 1; i++) {
    const hier = punkte[i]!
    const naechster = punkte[i + 1]!
    d += ` Q ${hier.x} ${hier.y} ${(hier.x + naechster.x) / 2} ${(hier.y + naechster.y) / 2}`
  }
  const letzter = punkte[punkte.length - 1]!
  d += ` L ${letzter.x} ${letzter.y}`
  return d
}

function Reisetext({ stand }: { stand: Reisestand }) {
  if (stand.amZiel) {
    return (
      <div className="reise__text">
        <div className="reise__station">{stand.station.name}</div>
        <p className="reise__info">{stand.station.info}</p>
      </div>
    )
  }

  return (
    <div className="reise__text">
      <div className="reise__station">
        {stand.station.name}
        <span className="reise__land"> · {stand.station.land}</span>
      </div>
      <p className="reise__info">{stand.station.info}</p>
      <p className="reise__naechste">
        Nächstes Ziel: <strong>{stand.naechste?.name}</strong>
        {' · noch '}
        {stand.nochRunden === 1 ? '1 Runde' : `${stand.nochRunden} Runden`}
      </p>
    </div>
  )
}
